// BuyView.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import Property from "../../components/Listing/Listing";
import Footer from "../../components/Footer/Footer";
import Loading from "../../components/Loading/Loading";
import PropertyAmazonMapView from "../../components/Map/PropertyAmazonMapView";
import PropertyFilter from "../../components/PropertyFilter/PropertyFilter";
import PaginationBar from "../PaginationBar/PaginationBar";
import styles from "./BuyView.module.css";
import { Button, Grid } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import { useAuth } from "../../contexts/AuthContext";

const BuyView = () => {
  const [allListings, setAllListings] = useState([]);
  const [mlsProperties, setMlsProperties] = useState([]);
  const [viewMode, setViewMode] = useState("map");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [dataSource, setDataSource] = useState("firstLand");
  const [addressInputState, setAddressInputState] = useState("");

  const { accessToken } = useAuth();

  const sortAndAlternateListings = (listings) => {
    const sortedListings = listings.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    const premium = sortedListings.filter((l) => l.is_premium);
    const normal = sortedListings.filter((l) => !l.is_premium);
    return [...premium, ...normal];
  };

  const toQueryString = (params) =>
    Object.entries(params)
      .map(
        ([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
      )
      .join("&");

  const fetchFirstLandListings = async (filters = []) => {
    try {
      const queryString = toQueryString(filters);
      setAddressInputState(filters.addressInput);
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/listings/allFilter?${queryString}`
      );

      if (response.status === 200 && response.data.success) {
        const listings = response.data.listings;

        if (accessToken) {
          const listingsWithFavorites = await fetchFavorites(listings);
          setAllListings(sortAndAlternateListings(listingsWithFavorites));
        } else {
          setAllListings(sortAndAlternateListings(listings));
        }
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  const fetchFavorites = async (listings) => {
    try {
      const favoritesResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/listings/favorites/`,
        {},
        {
          headers: { Authorization: accessToken },
        }
      );

      let favoriteListingIds = [];
      if (
        favoritesResponse.status === 200 &&
        favoritesResponse.data.success &&
        Array.isArray(favoritesResponse.data.favorites)
      ) {
        favoriteListingIds = favoritesResponse.data.favorites; // favorites is already an array of IDs
      }

      // Mark listings as favorite
      const listingsWithFavorites = listings.map((listing) => ({
        ...listing,
        isFavorite: favoriteListingIds.includes(listing.id),
      }));

      // console.log("Listings with favorites marked:", listingsWithFavorites);

      return listingsWithFavorites;
    } catch (error) {
      console.error("Error fetching favorites:", error);
      return listings; // Return original listings if there's an error
    }
  };

  const fetchMlsListings = async (page = 1, pageSize = 50) => {
    setDataSource("mls");

    try {
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/mls-properties/properties`,
        {
          params: { city: "tampa", state: "florida", page, pageSize },
        }
      );
      if (res.status === 200 && res.data.success) {
        setMlsProperties(res.data.mlsProperties || []);
        setAllListings(sortAndAlternateListings(res.data.mlsProperties || []));
        setCurrentPage(Number(res.data.currentPage));
        setItemsPerPage(Number(res.data.itemsPerPage));
        setTotalItems(Number(res.data.totalItems));
        setTotalPages(Number(res.data.totalPages));
      }
    } catch (err) {
      console.error("Error fetching MLS listings:", err);
    }
  };

  const handleApplyFilters = (filters, options = { triggerApi: false }) => {
    setAddressInputState(filters.addressInput || "");

    if (dataSource == "mls") {
      fetchMlsListings(currentPage, itemsPerPage);
    }

    if (dataSource == "firstLand") {
      fetchFirstLandListings(filters);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (dataSource === "mls") {
      fetchMlsListings(newPage, itemsPerPage);
    }
  };

  const handleDataSourceChange = (source) => {
    if (source === "mls") {
      fetchMlsListings(1, itemsPerPage);
    } else {
      setDataSource("firstLand");
      fetchFirstLandListings();
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchFirstLandListings().then(() => setIsLoading(false));
  }, []);

  return (
    <>
      <Navbar />
      <div className={styles.buyviewContainer}>
        <h1 className={styles.title}>Buy your dream home!</h1>

        <Grid container justifyContent="center" sx={{ marginBottom: "30px" }}>
          <div className={styles.toggleContainer}>
            <Button
              variant={viewMode === "list" ? "contained" : "outlined"}
              onClick={() => setViewMode("list")}
            >
              <ListIcon /> List
            </Button>
            <Button
              variant={viewMode === "map" ? "contained" : "outlined"}
              onClick={() => setViewMode("map")}
            >
              <MapIcon /> Map
            </Button>
          </div>
        </Grid>

        {isLoading ? (
          <div className={styles.buyAndSellLoading}>
            <Loading />
          </div>
        ) : (
          <>
            <PropertyFilter
              onApplyFilters={handleApplyFilters}
              onDataSourceChange={handleDataSourceChange}
              fetchMlsListings={fetchMlsListings}
              fetchFirstLandListings={fetchFirstLandListings}
              activeSource={dataSource}
            />

            <div className={styles.viewContent}>
              {viewMode === "list" ? (
                <div className={styles.propertyList}>
                  {allListings.map((listing, index) => (
                    <Property key={index} listing={listing} />
                  ))}
                </div>
              ) : (
                <PropertyAmazonMapView
                  listings={allListings}
                  addressInputState={addressInputState}
                />
              )}

              {dataSource === "mls" && totalPages > 1 && (
                <PaginationBar
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default BuyView;
